// src/pages/call-sessions/CallSessionsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import StatsCard from '../../components/common/StatsCard';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import PatientNameLink from '../../components/common/PatientNameLink';
import StatusBadge from '../../components/common/StatusBadge';
import ProgramFilter from '../../components/common/ProgramFilter';
import { useCallSessionContext } from '../../context/CallSessionContext';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { usePrograms } from '../../hooks/usePrograms';
import { enrollmentService } from '../../services/enrollmentService';
import { formatDuration, formatDate } from '../../utils/dateUtils';
import { buildCallSessionsFromEnrollments, calculateCallSessionStats } from '../../utils/callSessionUtils';
import { useSearchParams } from 'react-router-dom';

const CallSessionsPage = () => {
  const { medicalCenterId } = useMedicalCenter();
  const { openCallSessionDetails } = useCallSessionContext();
  const { programs, selectedProgramId, setSelectedProgramId } = usePrograms(medicalCenterId);
  const [callSessions, setCallSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [searchParams] = useSearchParams();
  const preselectDone = useRef(false);

  useEffect(() => {
    if (medicalCenterId) loadCallSessions();
  }, [medicalCenterId, selectedProgramId]);

  const loadCallSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const enrollments = await enrollmentService.getEnrollments(
        medicalCenterId,
        selectedProgramId || null
      );
      const processed = buildCallSessionsFromEnrollments(enrollments, {
        formatDuration,
        formatDate
      });

      setCallSessions(processed);
      setStats(calculateCallSessionStats(processed));

      const patientId = searchParams.get('patientId');
      const callSessionId = searchParams.get('callSessionId');

      if (!preselectDone.current && (patientId || callSessionId)) {
        preselectDone.current = true;

        let target = null;
        if (callSessionId) {
          target = processed.find(s => s.id === callSessionId);
        } else if (patientId) {
          target = processed.find(s => s.patient?.id === patientId);
        }

        if (target && target.patient?.id) {
          const enrollment = { id: target.enrollment_id, medical_center_id: medicalCenterId };
          openCallSessionDetails(target.patient, target.id, enrollment);
        }
      }
    } catch (err) {
      console.error('Error loading call sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (callSession) => {
    if (!callSession.patient?.id) {
      console.error('No patient ID available');
      return;
    }
    const enrollment = { id: callSession.enrollment_id, medical_center_id: medicalCenterId };
    openCallSessionDetails(callSession.patient, callSession.id, enrollment);
  };

  const renderCallSessionRow = (callSession, index) => (
    <tr key={`${callSession.id}-${index}`} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <PatientNameLink
          patient={callSession.patient}
          callSessionId={callSession.id}
          enrollment={{ id: callSession.enrollment_id, medical_center_id: medicalCenterId }}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            callSession.patient_assessment_flag === 'stable'
              ? 'bg-green-200 text-green-800'
              : callSession.patient_assessment_flag === 'critical'
              ? 'bg-red-300 text-red-900'
              : callSession.patient_assessment_flag === 'follow-up'
              ? 'bg-yellow-200 text-yellow-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {callSession.patient_assessment_flag
            ? callSession.patient_assessment_flag.charAt(0).toUpperCase() +
              callSession.patient_assessment_flag.slice(1)
            : 'Unknown'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {callSession.call_date_display}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {callSession.duration_display}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={callSession.status} type="" />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          <Button onClick={() => handleViewClick(callSession)} size="sm" variant="outline">
            View
          </Button>
        </div>
      </td>
    </tr>
  );

  if (loading) return <LoadingSpinner />;
  if (error)
    return (
      <ErrorDisplay error={error} onRetry={loadCallSessions} title="Error Loading Call Sessions" />
    );

  return (
    <div className="space-y-6">
      <PageHeader title="Call Sessions" subtitle="View and manage call sessions" showAction={false} />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <StatsCard title="Total Sessions" value={stats.total || 0} subtitle="All call sessions" />
        <StatsCard title="Completed" value={stats.completed || 0} subtitle="Successful calls" />
        <StatsCard title="Failed" value={stats.failed || 0} subtitle="Failed calls" />
        <StatsCard title="Initiated" value={stats.initiated || 0} subtitle="Pending calls" />
        <StatsCard title="Rescheduled" value={stats.rescheduled || 0} subtitle="Rescheduled calls" />
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Call Session List</h2>
          <ProgramFilter
            programs={programs}
            selectedProgramId={selectedProgramId}
            onChange={setSelectedProgramId}
          />
        </div>
        <Table
          headers={['Patient ', 'Patient Assessment', 'Call Date', 'Duration', 'Status', 'Actions']}
          data={callSessions}
          renderRow={renderCallSessionRow}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CallSessionsPage;